import React, { useEffect, useState, Suspense } from "react";
import { useTonAddress } from "@tonconnect/ui-react";
import { useDispatch, useSelector } from "react-redux";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import WebApp from "@twa-dev/sdk";
import Page from "./wrappers/Page";
import KredafiPage from "./kredafi/KredafiPage";
import routes from "./routes";
import "./i18n";
import { Spin, Typography, Button } from "antd";
import Banner from "./components/banner";
import LandingPage from "./components/LandingPage/LandingPage";
import { getInitialLanguage } from "./constants/languages";
import "./App.css";
import { saveWalletToDatabase, addUser, validateHash } from "./helpers/api";
import { useTranslation } from "react-i18next";
import { RootState } from "./services/store";
import useAxiosSetup from "./hooks/useAxiosSetup";
import PlatformBackButton from "./components/PlatformBackButton";
import TonReturnStrategySync from "./components/TonReturnStrategySync";
import AuthGate from "./pages/Auth/AuthGate";
import { ReownProvider } from "./providers/ReownProvider";
import { MultiWalletProvider } from "./providers/MultiWalletProvider";
import AppLock from "./components/Security/AppLock";
import { clearSession, getStoredUserId, hasWebSession, saveSession } from "./services/auth";
import {
  BOT_LINK,
  getPlatformLanguage,
  getReferralCode,
  initPlatformChrome,
  isTelegramMiniApp,
  openTelegramLink,
} from "./utils/platform";
import { applyKredafiDocumentMeta, isKredafiShell } from "./utils/brand";
import {
  setUserData,
  setLoading,
  setStartParam,
  initializeUserData,
} from "./services/slices/userSlice";
import { WebAppUser, UserResponse } from "./types";

// import('eruda').then(eruda => eruda.default.init());

const { Text } = Typography;

const App: React.FC = () => {
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();
  const [isHashValid, setIsHashValid] = useState(false);
  const { userData, isLoading, startParam } = useSelector(
    (state: RootState) => state.user
  );
  const userFriendlyAddress = useTonAddress();
  const [banned, setBanned] = useState(false);
  const [userIsNotValid, setUserIsNotValid] = useState(false);
  // В браузере до входа показываем лендинг и экран входа вместо приложения.
  const [needsAuth, setNeedsAuth] = useState(false);

  useAxiosSetup();

  useEffect(() => {
    applyKredafiDocumentMeta();
  }, []);

  const applyLanguage = (languageCode?: string) => {
    const initialLang = getInitialLanguage(languageCode);
    if (i18n.language !== initialLang) {
      i18n.changeLanguage(initialLang);
    }
    try {
      document.documentElement.lang = initialLang;
      document.documentElement.dir = initialLang === "ar" ? "rtl" : "ltr";
    } catch (e) {
      // Ignore
    }
  };

  /** Общий вход в приложение для веба: и после логина, и при возврате с сессией. */
  const startWebSession = (userId: number | null) => {
    dispatch(
      setUserData(
        initializeUserData({
          id: userId || 0,
          // Настоящие имя и аватар придут в ответе add-user.php.
          first_name: "",
        } as WebAppUser)
      )
    );
    setNeedsAuth(false);
    setIsHashValid(true);
  };

  useEffect(() => {
    // Путь мини-аппа оставлен без изменений: подпись Telegram проверяется так же,
    // как и раньше. Веб-сессии этот код не касается.
    if (isTelegramMiniApp) {
      const initData = WebApp.initData;
      const user = WebApp.initDataUnsafe.user as WebAppUser;
      const startParam = WebApp.initDataUnsafe.start_param as string;

      const initialUserData: UserResponse = initializeUserData(user);

      dispatch(setUserData(initialUserData));
      applyLanguage(user?.language_code);

      if (startParam) {
        dispatch(setStartParam(startParam));
      }

      validateHash(initData).then((isValid) => {
        setIsHashValid(isValid);
        if (isValid) {
          initPlatformChrome();
        } else {
          setUserIsNotValid(true);
        }
      });
      return;
    }

    // Браузер и установленное приложение: личность подтверждает не Telegram,
    // а сессия, выданная эндпоинтами /api/auth/* при входе.
    applyLanguage(getPlatformLanguage());

    const referral = getReferralCode();
    if (referral) {
      dispatch(setStartParam(referral));
    }

    // Проверяем возврат из единого SSO шлюза авторизации (subdomain support)
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const ssoAuthToken = searchParams.get("auth_token");
      const ssoRefreshToken = searchParams.get("refresh_token");
      const ssoUserId = searchParams.get("user_id");

      if (ssoAuthToken && ssoRefreshToken && ssoUserId) {
        saveSession({
          status: "ok",
          user_id: Number(ssoUserId),
          created: false,
          token: ssoAuthToken,
          refresh_token: ssoRefreshToken,
          expires_in: 3600,
        });

        searchParams.delete("auth_token");
        searchParams.delete("refresh_token");
        searchParams.delete("user_id");
        const remaining = searchParams.toString();
        const cleanUrl =
          window.location.pathname +
          (remaining ? `?${remaining}` : "") +
          window.location.hash;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    } catch (e) {
      console.warn("[Auth] Failed to parse URL tokens", e);
    }

    // Слушатель для SSO авторизации через popup
    const handleSsoMessage = (event: MessageEvent) => {
      if (
        event.data &&
        event.data.type === "KREDAFI_SSO_AUTH" &&
        event.data.session
      ) {
        saveSession(event.data.session);
        startWebSession(event.data.session.user_id);
      }
    };
    window.addEventListener("message", handleSsoMessage);

    if (hasWebSession()) {
      startWebSession(getStoredUserId());
    } else {
      dispatch(setLoading(false));
      setNeedsAuth(true);
    }

    return () => {
      window.removeEventListener("message", handleSsoMessage);
    };
  }, []);


  useEffect(() => {
    if (isHashValid && userData) {
      dispatch(setLoading(true));
      addUser(userData, startParam || "", isTelegramMiniApp ? WebApp.initData : "")
        .then((response) => {
          localStorage.setItem('auth_token', `Bearer ${response.token}`);

          dispatch(
            setUserData({
              ...userData!,
              ...response,
              // В мини-аппе имя из Telegram — источник истины: в базе оно
              // записано при регистрации и может отставать. В браузере
              // наоборот: кроме базы, брать его неоткуда.
              ...(isTelegramMiniApp
                ? {
                    first_name: userData!.first_name,
                    last_name: userData!.last_name,
                    username: userData!.username,
                  }
                : {}),
            })
          );
          if (response.status === 1) {
            setBanned(true);
          }
        })
        .catch((error) => {
          console.error("Error adding user:", error);

          // Отказ без ответа сервера — это сеть или отвергнутый браузером
          // кросс-доменный запрос. Раньше он проходил молча, и приложение
          // оставалось с настройками по умолчанию: пустые ставки на экране
          // выдачи займа выглядели как «0.00%», а не как ошибка загрузки.
          if (!error?.response) {
            console.error(
              "[App] add-user.php не ответил: настройки платформы (ставки) остались значениями по умолчанию"
            );
          }

          // В вебе 401 здесь означает, что сессия кончилась совсем: интерцептор
          // уже пробовал обменять refresh-токен и не смог. Показываем вход,
          // а не пустое приложение с нулевыми балансами.
          if (!isTelegramMiniApp && error?.response?.status === 401) {
            clearSession();
            setIsHashValid(false);
            setNeedsAuth(true);
          }
        })
        .finally(() => dispatch(setLoading(false)));
    }
  }, [isHashValid]);

  useEffect(() => {
    if (userFriendlyAddress && userData && userData.token) {
      saveWalletToDatabase({
        userId: userData.id,
        walletAddress: userFriendlyAddress,
      });
    }
  }, [userFriendlyAddress]);

  useEffect(() => {
    dispatch({ type: "websocket/connect" });
  }, [dispatch]);

  // Браузер до входа: лендинг и экран входа вместо приложения.
  if (needsAuth) {
    return (
      <AuthGate
        onAuthenticated={({ userId }) => {
          dispatch(setLoading(true));
          startWebSession(userId);
        }}
      />
    );
  }

  // Мини-апп, но подпись Telegram не сошлась: внутри Telegram чинить это нечем.
  if (userIsNotValid) {
    return <LandingPage />;
  }

  if (!isHashValid || isLoading) {
    return (
      <div className="loader-container">
        <Spin size="large" />
      </div>
    );
  }
  if (banned) {
    return (
      <div className="banned-container">
        <div className="banned-content">
          <div className="banned-icon">
            🚫
          </div>
          <div className="banned-title">
            {t("Account Blocked")}
          </div>
          <div className="banned-support">
            {t("Contact support for more information")}
          </div>
          <Button
            type="primary"
            size="large"
            onClick={() =>
              openTelegramLink("https://t.me/webtransferpartners/4759")
            }
            className="banned-button"
          >
            {t("Contact Support")}
          </Button>
        </div>
      </div>
    );
  }
  const isBannerEnabled = userData!.settings.banner_enabled === "on";
  const kredafi = isKredafiShell();
  const Shell = kredafi ? KredafiPage : Page;

  const appContent = (
    <div className={kredafi ? "kredafi-root" : "App"}>
      <AppLock />
      <Router>
        {!isTelegramMiniApp && <TonReturnStrategySync />}
        {!kredafi && isBannerEnabled && <Banner />}
        <Shell>
          <Suspense
            fallback={
              <div className="loader-container">
                <Spin size="large" />
              </div>
            }
          >
            <Routes>
              {routes.map(({ path, component: Component }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <>
                      {!kredafi && path !== "/" && path !== "/menu" && (
                        <PlatformBackButton />
                      )}
                      <Component />
                    </>
                  }
                />
              ))}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Shell>
      </Router>
    </div>
  );

  if (isTelegramMiniApp) {
    return appContent;
  }

  return (
    <ReownProvider>
      <MultiWalletProvider>{appContent}</MultiWalletProvider>
    </ReownProvider>
  );
};

export default App;
